import videoUtils from "../custom/video-utils"; // Ensure the path is correct

const playHTML5Video = async (video) => {
    try {
        await video.play();
    } catch (e) {
        /* Autoplay or policy blocked */
    }
};

const copyImageVideo = () => {

    const videoBlocks = document.querySelectorAll(".copy-image-video__media");
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const block = entry.target;
                void initializeVideoBlock(block);
                observer.unobserve(block); // Unobserve after initialization
            }
        });
    });
    videoBlocks.forEach((block) => observer.observe(block));
};

// Initialize a single video within the block
const initializeVideoBlock = async (block) => {

	const controls = block.querySelector(".video-controls");
    const playButton = controls?.querySelector(".play-button");
    const muteButton = controls?.querySelector(".mute-button");
    const fullButton = controls?.querySelector(".full-button");
    const videoWrapper = block.querySelector(".copy-image-video__media-video");

    if (!videoWrapper) {
        return;
    }

    const applyPosterFallback = () => {
        block.classList.add("copy-image-video__media--video-unavailable");
        videoWrapper.classList.remove("visible");
        videoWrapper.setAttribute("hidden", "");
    };

	//Show Video Wrapper
	videoWrapper.classList.add("visible");

    const video = videoWrapper.querySelector("video");
    const iframe = videoWrapper.querySelector("iframe");

	// Show controls (fade-in)
	const showControls = () => {
        if (controls) {
            controls.classList.add("visible");
        }
    };

	// HTML5 Video: Show controls after video is loaded
	if (video) {
        video.addEventListener("loadeddata", showControls);
    }

    // Vimeo: wait for player; on permission/embed errors fall back to still image
    if (iframe && iframe.src.includes("vimeo.com")) {
        iframe.setAttribute("loading", "lazy");
        try {
            await videoUtils.loadVimeoAPI();
        } catch (e) {
            applyPosterFallback();
            return;
        }
        const player = videoUtils.getVimeoPlayer(iframe);
        if (!player) {
            applyPosterFallback();
            return;
        }
        try {
            await player.ready();
        } catch (e) {
            videoUtils.discardVimeoPlayer(iframe);
            applyPosterFallback();
            return;
        }
        showControls();
    } else if (iframe) {
        iframe.setAttribute("loading", "lazy");
        if (iframe.src.includes("youtube.com")) {
            /* Controls shown from setupIframeListeners when player fires */
        }
    }

    // Set a fixed aspect ratio to prevent layout shifts
    videoWrapper.style.aspectRatio = "16/9";

    // Update play button visibility based on playback state
    const updatePlayButtonVisibility = (isPaused) => {
        if (!playButton) {
            return;
        }
        if (isPaused) {
            playButton.classList.add("visible");
            playButton.classList.remove("playing");
            playButton.textContent = "Play";
        } else {
            playButton.classList.remove("visible");
            playButton.classList.add("playing");
            playButton.textContent = "Pause";
        }
    };

    // Synchronize mute button state with video/iframe
    const syncMuteButtonState = async () => {
        if (!muteButton) {
            return;
        }
        let isMuted;
        if (video) {
            isMuted = video.muted;
        } else if (iframe) {
            if (iframe.src.includes("vimeo.com")) {
                const player = videoUtils.getVimeoPlayer(iframe);
                if (!player) {
                    return;
                }
                const currentVolume = await player.getVolume();
                isMuted = currentVolume === 0;
            } else if (iframe.src.includes("youtube.com")) {
                const player = videoUtils.getYouTubePlayer(iframe);
                const currentVolume = player.getVolume();
                isMuted = currentVolume === 0;
            }
        }
        muteButton.classList.toggle("mute", isMuted);
        muteButton.textContent = isMuted ? "Unmute" : "Mute";
    };

	const toggleMute = async (video, iframe, muteButton) => {
		try {
			// Check current mute state
			let isMuted;

			if (video) {
				// HTML5 video
				isMuted = video.muted;
				video.muted = !isMuted; // Toggle mute state
			} else if (iframe) {
				if (iframe.src.includes("vimeo.com")) {
					// Vimeo iframe
					const player = videoUtils.getVimeoPlayer(iframe);
					if (!player) {
						return;
					}
					const currentVolume = await player.getVolume();
					isMuted = currentVolume === 0; // Consider muted if volume is 0
					await player.setVolume(isMuted ? 1 : 0); // Toggle volume
				} else if (iframe.src.includes("youtube.com")) {
					// YouTube iframe
					const player = await videoUtils.getYouTubePlayer(iframe);
					const currentVolume = player.getVolume();
					isMuted = currentVolume === 0; // Consider muted if volume is 0
					player.setVolume(isMuted ? 100 : 0); // Toggle volume
				}
			}

			// Update mute button state
			muteButton.classList.toggle("mute", !isMuted); // Add 'un-mute' class if now muted
			muteButton.textContent = !isMuted ? "Unmute" : "Mute"; // Update button text
		} catch (error) {

		}
	};

    // Detect autoplay and set initial state
    const detectAutoplay = async () => {
        if (video) {
            updatePlayButtonVisibility(video.paused);
			video.muted = true; // Ensure autoplayed video starts muted
        } else if (iframe) {
            if (iframe.src.includes("vimeo.com")) {
				try {
					const player = videoUtils.getVimeoPlayer(iframe);
					if (!player) {
						applyPosterFallback();
						return;
					}
					const isPlaying = await videoUtils.isVimeoPlaying(iframe);
					await player.setVolume(0); // Start muted
					updatePlayButtonVisibility(!isPlaying);
				} catch (e) {
					videoUtils.discardVimeoPlayer(iframe);
					applyPosterFallback();
					return;
				}
            } else if (iframe.src.includes("youtube.com")) {
                const isPlaying = await videoUtils.isYouTubePlaying(iframe);
                updatePlayButtonVisibility(!isPlaying);
				const player = videoUtils.getYouTubePlayer(iframe);
				if (player) {
					player.mute(); // Start muted
				}
            }
        }
		// Sync mute button state after ensuring video starts muted
        await syncMuteButtonState();
    };

    // Event listeners for iframe
    const setupIframeListeners = async () => {
        if (iframe && iframe.src.includes("vimeo.com")) {
            try {
                await videoUtils.loadVimeoAPI();
            } catch (e) {
                return;
            }
            const player = videoUtils.getVimeoPlayer(iframe);
            if (!player) {
                return;
            }
            player.on("play", () => updatePlayButtonVisibility(false));
            player.on("pause", () => updatePlayButtonVisibility(true));
        } else if (iframe && iframe.src.includes("youtube.com")) {
            await videoUtils.initializeYouTubePlayer(iframe, {
                onStateChange: (event) => {
                    if (event.data === YT.PlayerState.PAUSED) {
                        updatePlayButtonVisibility(true);
                    } else if (event.data === YT.PlayerState.PLAYING) {
                        updatePlayButtonVisibility(false);
                    }
                },
            });
        }
    };

    // Play/Pause Video Handler
    const toggleVideoPlayPause = async () => {
        if (video) {
            if (video.paused) {
                await playHTML5Video(video);
                updatePlayButtonVisibility(false);
            } else {
                video.pause();
                updatePlayButtonVisibility(true);
            }
        } else if (iframe) {
            if (iframe.src.includes("vimeo.com")) {
                const isPlaying = await videoUtils.isVimeoPlaying(iframe);
                if (isPlaying) {
                    await videoUtils.pauseVimeo(iframe);
                    updatePlayButtonVisibility(true);
                } else {
                    await videoUtils.playAndControlVimeo(iframe);
                    updatePlayButtonVisibility(false);
                }
            } else if (iframe.src.includes("youtube.com")) {
                const isPlaying = await videoUtils.isYouTubePlaying(iframe);
                if (isPlaying) {
                    await videoUtils.pauseYouTube(iframe);
                    updatePlayButtonVisibility(true);
                } else {
                    await videoUtils.playYouTube(iframe);
                    updatePlayButtonVisibility(false);
                }
            }
        }
    };

    // Fullscreen and FancyBox Handler
    const fullScreenPlayPauseHandler = () => {
        if (fullButton) {
            fullButton.classList.add("active");
        }

        if (playButton && playButton.classList.contains("playing")) {
            toggleVideoPlayPause(); // Pause video and toggle button state
        }

        videoUtils.setupFancyBoxCloseHandler(iframe || video, video ? "HTML5" : "Vimeo", playButton, () => {
            if (playButton && !playButton.classList.contains("playing")) {
                toggleVideoPlayPause(); // Resume video and toggle button state
            }
        });
    };

    // Attach event listeners
    if (muteButton) muteButton.addEventListener("click", () => toggleMute(video, iframe, muteButton));
    if (playButton) playButton.addEventListener("click", toggleVideoPlayPause);
    if (fullButton) fullButton.addEventListener("click", fullScreenPlayPauseHandler);

    await setupIframeListeners();
    await detectAutoplay();

    // HTML5 Video Event Listeners
    if (video) {
        video.addEventListener("play", () => updatePlayButtonVisibility(false));
        video.addEventListener("pause", () => updatePlayButtonVisibility(true));
    }
};

// Preload the first video for better LCP (HTML5 only – do not preload iframe/Vimeo/YouTube)
const preloadFirstVideo = () => {
    const firstBlock = document.querySelector(".copy-image-video__media-video");
    if (!firstBlock) return;
    const video = firstBlock.querySelector("video");
    if (!video || !video.src) return;
    // Only preload direct video sources; iframe URLs (Vimeo/YouTube) must not be preloaded
    // with link preload – the browser treats them as unused and triggers CWV warnings.
    const preloadLink = document.createElement("link");
    preloadLink.rel = "preload";
    preloadLink.as = "video";
    preloadLink.href = video.src;
    document.head.appendChild(preloadLink);
};

// Initialize blockVideo on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
    preloadFirstVideo();
    copyImageVideo();
    // Select all blocks and make them visible
    const blocks = document.querySelectorAll(".block-copy-image-video");
    blocks.forEach((block) => {
        requestAnimationFrame(() => {
            block.classList.add("visible");
        });
    });
});
